import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { LoaderCircleIcon } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import type { TariffLineView } from "../../../../shared/api.ts";
import { formatCode } from "../../../../shared/uktzed.ts";
import { Command, type CommandItem } from "@/components/ui/command.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { useDebounced } from "@/lib/hooks.ts";
import { cn } from "@/lib/utils.ts";

const INDENT: Record<TariffLineView["digits"], string> = { 4: "pl-0", 6: "pl-3", 8: "pl-6", 10: "pl-9" };

function TariffRow({ line }: { line: TariffLineView }) {
  const { t } = useI18n();
  const selectable = line.digits === 10;
  return (
    <div className={cn("flex min-w-0 items-baseline gap-2.5", INDENT[line.digits])}>
      <span className={cn("code-mono shrink-0 text-[12.5px]", selectable ? "font-semibold text-foreground" : "text-muted-foreground")}>
        {line.printed || formatCode(line.code)}
      </span>
      <span className={cn("min-w-0 flex-1 truncate", selectable ? "text-foreground/90" : "text-muted-foreground")} title={line.title}>
        {line.title}
      </span>
      {selectable && line.duty ? (
        <span className="hidden shrink-0 text-[11px] text-danger sm:inline">{line.duty === "Not found" ? t.common.notFound : line.duty}</span>
      ) : null}
    </div>
  );
}

/** Search in `GET /api/tariff/lookup?q=`; only 10-digit lines can be picked. */
export function TariffSearch({ onPick, autoFocus = true, className }: { onPick: (line: TariffLineView) => void; autoFocus?: boolean; className?: string }) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query.trim(), 220);
  const enabled = debounced.length >= 2;
  const lookup = useQuery({
    queryKey: queryKeys.tariff(debounced),
    queryFn: ({ signal }) => api.tariffLookup(debounced, signal),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  const items = useMemo<Array<CommandItem<TariffLineView>>>(
    () =>
      (enabled ? (lookup.data ?? []) : []).map((line) => ({
        id: `${line.code}-${line.digits}`,
        value: line,
        disabled: line.digits !== 10,
        content: <TariffRow line={line} />,
      })),
    [enabled, lookup.data],
  );

  let status: ReactNode = null;
  if (!enabled) status = t.combobox.hint;
  else if (lookup.isError) status = t.combobox.error;
  else if (lookup.isPending)
    status = (
      <span className="inline-flex items-center gap-2">
        <LoaderCircleIcon className="size-3.5 animate-spin" aria-hidden />
        {t.combobox.loading}
      </span>
    );
  else if (items.length === 0) status = t.combobox.empty;

  return (
    <div className={cn("flex min-w-0 flex-col", className)}>
      <Command
        query={query}
        onQueryChange={setQuery}
        items={items}
        onSelect={onPick}
        placeholder={t.combobox.placeholder}
        label={t.combobox.label}
        status={status}
        autoFocus={autoFocus}
      />
      <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">{t.combobox.onlyTen}</p>
    </div>
  );
}

/** A trigger that opens the tariff search in a popover. */
export function CodePickerPopover({
  children,
  onPick,
  open,
  onOpenChange,
}: {
  children: ReactNode;
  onPick: (line: TariffLineView) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[min(30rem,calc(100vw-2rem))] p-0" align="start" collisionPadding={16}>
        <TariffSearch
          onPick={(line) => {
            onPick(line);
            onOpenChange(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
